import { toast } from "@components/utils/Toaster";
import useContactLinks from "@hooks/useContactLinks";
import { useLoginSubmit } from "@hooks/useLoginSubmit";
import { useMemberSettings } from "@hooks/useMemberSettings";
import { AuthToken, cn, LoginGrantType, PropsWithClassName } from "kz-ui-sdk";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import GoogleAuthButton from "./GoogleAuthButton";

interface AuthSocialProps extends PropsWithClassName {
  mode: "login" | "register";
  title?: string;
  onLoginSuccess?: (token?: AuthToken) => void;
}

const AuthSocial = ({ mode, title, className, onLoginSuccess }: AuthSocialProps) => {
  const { isSupportAuthSocial, googleAuthEnabled } = useMemberSettings();
  const { t } = useTranslation();
  const { openContact } = useContactLinks();

  const { handleLogin, isLoading: isLoginLoading } = useLoginSubmit({
    onLoginSuccess,
  });

  const handleOpenContactCsDialog = useCallback(
    (event: React.MouseEvent) => {
      event.preventDefault();
      openContact();
    },
    [openContact],
  );

  if (!isSupportAuthSocial) {
    return null;
  }

  const handleCommonError = () => {
    toast.warningWithClose(
      <span className="">
        <span>{t("something went wrong. please retry or")}</span>{" "}
        <span
          onClick={handleOpenContactCsDialog}
          className="cursor-pointer text-content-soft underline"
        >
          {t("contact cs")}
        </span>
      </span>,
      {
        duration: 8000,
      },
    );
  };

  const handleGoogleLoginSuccess = (credential: string) => {
    handleLogin(credential, credential, LoginGrantType.Google);
  };

  const handleGoogleLoginError = () => {
    handleCommonError();
  };

  return (
    <div className={cn("flex w-full flex-col items-center", className)}>
      {!!title && <p className="mb-2 text-base leading-6 text-content-soft">{title}</p>}
      {googleAuthEnabled && (
        <GoogleAuthButton
          mode={mode}
          loading={isLoginLoading}
          onOAuthLoginSuccess={handleGoogleLoginSuccess}
          onOAuthLoginError={handleGoogleLoginError}
        />
      )}
    </div>
  );
};

export default AuthSocial;
